/**
 * Деньги по НФТ-09: «1 234 567 ₸». В API суммы — целые тиыны (1 ₸ = 100 тиынов,
 * docs/contracts/backend-conventions.md). Разряды и знак тенге отделяются неразрывным пробелом,
 * чтобы сумма не разрывалась переносом строки.
 */
export const NBSP = '\u00A0';
export const TENGE_SIGN = '₸';
const MINUS = '−';

export type TiynDisplay = 'auto' | 'always' | 'never';

export interface MoneyFormatOptions {
  /** Тиыны: «auto» — только если есть (по умолчанию), «always» — всегда «,00», «never» — округлить до тенге. */
  tiyn?: TiynDisplay;
  /** Добавлять знак ₸ (по умолчанию — да). */
  currency?: boolean;
  /** Показывать «+» у положительных сумм (изменения, дельты). */
  signed?: boolean;
}

/** Группировка разрядов по три неразрывными пробелами: 1234567 → «1 234 567». */
export function groupDigits(digits: string, separator: string = NBSP): string {
  const clean = digits.replace(/^0+(?=\d)/, '');
  let result = '';
  for (let index = 0; index < clean.length; index += 1) {
    const fromEnd = clean.length - index;
    result += clean[index];
    if (fromEnd > 1 && fromEnd % 3 === 1) result += separator;
  }
  return result;
}

function toBigInt(value: number | bigint): bigint {
  if (typeof value === 'bigint') return value;
  if (!Number.isFinite(value)) throw new RangeError(`Invalid money amount: ${value}`);
  return BigInt(Math.round(value));
}

/** Сумма в тиынах → «1 234 567 ₸» / «1 234 567,50 ₸». */
export function formatMoney(tiyn: number | bigint, options: MoneyFormatOptions = {}): string {
  const mode = options.tiyn ?? 'auto';
  let amount = toBigInt(tiyn);
  const negative = amount < 0n;
  if (negative) amount = -amount;
  if (mode === 'never') amount = ((amount + 50n) / 100n) * 100n;
  const tenge = amount / 100n;
  const fraction = amount % 100n;
  let text = groupDigits(tenge.toString());
  if (mode === 'always' || (mode === 'auto' && fraction !== 0n)) {
    text += `,${fraction.toString().padStart(2, '0')}`;
  }
  const sign = negative ? MINUS : options.signed && amount !== 0n ? '+' : '';
  const currency = options.currency === false ? '' : `${NBSP}${TENGE_SIGN}`;
  return `${sign}${text}${currency}`;
}

/** Текст поля суммы при редактировании (без знака ₸): 123456750 → «1 234 567,50». */
export function formatMoneyInput(tiyn: number | bigint | null | undefined): string {
  if (tiyn === null || tiyn === undefined) return '';
  return formatMoney(tiyn, { currency: false });
}

/**
 * Разбор введённой суммы: «1 234 567,5», «1234567.50», «1 234 567 ₸» → тиыны.
 * Возвращает null для пустой или некорректной строки (больше двух знаков после запятой — ошибка).
 */
export function parseMoneyInput(value: string): number | null {
  const compact = value.replace(/[\s\u00A0\u202F₸]/g, '').replace(MINUS, '-');
  if (compact === '') return null;
  const match = /^(-?)(\d+)(?:[.,](\d{0,2}))?$/.exec(compact);
  if (!match) return null;
  const sign = match[1] === '-' ? -1 : 1;
  const whole = match[2] ?? '0';
  const fraction = (match[3] ?? '').padEnd(2, '0');
  const tiyn = Number(whole) * 100 + Number(fraction);
  if (!Number.isSafeInteger(tiyn)) return null;
  return sign * tiyn;
}

/** Тенге → тиыны (для констант в коде: tenge(150_000) === 15_000_000). */
export function tenge(amount: number): number {
  return Math.round(amount * 100);
}
